import { useState } from "react";
import { useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { nativeSelectClass } from "../tasks/taskData";
import { reasonsFor, useLostReasons } from "./dealData";

export interface LostChoice {
  lost_reason_id: number;
  lost_reason: string | null;
}

// Asked when a deal goes to a lost stage: the database refuses a lost deal
// without a reason, and without a note when the reason asks for one (D-036).
// Cancel leaves the deal where it was.
export const LostReasonDialog = ({
  pipelineId,
  dealName,
  onConfirm,
  onCancel,
}: {
  pipelineId: number;
  dealName?: string;
  onConfirm: (choice: LostChoice) => void;
  onCancel: () => void;
}) => {
  const translate = useTranslate();
  const { data: reasons = [] } = useLostReasons();
  const choices = reasonsFor(reasons, pipelineId);
  const [reasonId, setReasonId] = useState("");
  const [note, setNote] = useState("");
  const reason = choices.find((item) => String(item.id) === reasonId);
  const isMissingNote = !!reason?.requires_note && !note.trim();

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{translate("tenax.deals.lost.title")}</DialogTitle>
          <DialogDescription>
            {dealName
              ? translate("tenax.deals.lost.hint_deal", { name: dealName })
              : translate("tenax.deals.lost.hint")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label htmlFor="tenax-lost-reason">
            {translate("tenax.deals.lost.reason")}
          </Label>
          <select
            id="tenax-lost-reason"
            className={nativeSelectClass}
            value={reasonId}
            onChange={(event) => setReasonId(event.target.value)}
          >
            <option value="">{translate("tenax.deals.lost.choose")}</option>
            {choices.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
          <Label htmlFor="tenax-lost-note">
            {translate(
              reason?.requires_note
                ? "tenax.deals.lost.note_required"
                : "tenax.deals.lost.note",
            )}
          </Label>
          <Textarea
            id="tenax-lost-note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            {translate("ra.action.cancel")}
          </Button>
          <Button
            type="button"
            disabled={!reason || isMissingNote}
            onClick={() =>
              reason &&
              onConfirm({
                lost_reason_id: reason.id,
                lost_reason: note.trim() || null,
              })
            }
          >
            {translate("tenax.deals.lost.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
